import React, { type ReactElement, useEffect, useRef } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';

import { TRACKER_ATTR, ZONE_ATTR, ZONE_TYPE_ATTR } from './constants';
import type { ViewDeadAirContentType } from './types';
import { useViewDeadAirContext } from './useViewDeadAirContext';

type ViewDeadAirTrackerProps = {
	id: string;
	contentType: ViewDeadAirContentType;
	children: ReactElement;
};

export function ViewDeadAirTracker({
	id,
	contentType,
	children,
}: ViewDeadAirTrackerProps): React.JSX.Element {
	if (!fg('rovo_chat_track_view_dead_air')) {
		return <>{children}</>;
	}

	return (
		<ViewDeadAirTrackerInner id={id} contentType={contentType}>
			{children}
		</ViewDeadAirTrackerInner>
	);
}

function ViewDeadAirTrackerInner({ id, contentType, children }: ViewDeadAirTrackerProps) {
	const broker = useViewDeadAirContext();
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const el = containerRef.current;
		if (!el || !broker) {
			return;
		}

		const observer = new MutationObserver((mutations) => {
			for (const mutation of mutations) {
				const target =
					mutation.target instanceof Element ? mutation.target : mutation.target.parentElement;

				// Skip if mutation is inside a nested tracker (handled by inner tracker)
				const closestTracker = target?.closest(`[${TRACKER_ATTR}]`);
				if (closestTracker && closestTracker !== el) {
					continue;
				}

				// Check if mutation is inside a zone
				const zone = target?.closest(`[${ZONE_ATTR}]`);
				if (zone) {
					const zoneId = zone.getAttribute(ZONE_ATTR);
					const zoneType =
						(zone.getAttribute(ZONE_TYPE_ATTR) as ViewDeadAirContentType) || 'stream';
					if (zoneId) {
						broker.mark({ id: zoneId, contentType: zoneType });
						return;
					}
				}

				// Normal mutation in the tracker's own area
				broker.mark({ id, contentType });
				return;
			}
		});

		observer.observe(el, {
			childList: true,
			characterData: true,
			subtree: true,
		});

		return () => observer.disconnect();
	}, [id, contentType, broker]);

	return (
		<div ref={containerRef} {...{ [TRACKER_ATTR]: true }}>
			{children}
		</div>
	);
}
